// 25-nutrient panel — the "Cronometer-grade" micronutrient set we promised on
// the waitlist. Used by the AI estimator + Open Food Facts + (eventually) the
// curated generic foods database.
//
// All fields are OPTIONAL. The data quality varies wildly by source:
//   - AI photo estimates: confident on macros, decent on micros for common
//     foods, often empty for esoteric/mixed meals
//   - Open Food Facts: filled in for branded products with nutrition labels;
//     missing for many small-brand items
//   - Curated generic foods: nutrient data not yet added — coming
//
// The UI gracefully shows "—" for missing fields and a percent-of-RDA bar
// when data is present.

import type { LoggedMeal, MealEstimate } from "./types";

/**
 * Canonical 25-nutrient panel. Units are noted per-field below — kept in
 * separate units (rather than normalizing to mg) because that's what users
 * read on nutrition labels.
 *
 * Vitamins (12): A (μg RAE), B1 (mg), B2 (mg), B3 (mg NE), B6 (mg),
 *                B9 / folate (μg DFE), B12 (μg), C (mg), D (μg), E (mg),
 *                K (μg)
 * Minerals (11): iron, calcium, magnesium, zinc, potassium, sodium,
 *                phosphorus, copper, manganese (all mg unless noted),
 *                selenium (μg), iodine (μg)
 * Other (2):     sugar (g), saturated fat (g)
 */
export interface NutrientPanel {
  // Vitamins
  vit_a_ug?: number;
  vit_b1_mg?: number;
  vit_b2_mg?: number;
  vit_b3_mg?: number;
  vit_b6_mg?: number;
  vit_b9_ug?: number;
  vit_b12_ug?: number;
  vit_c_mg?: number;
  vit_d_ug?: number;
  vit_e_mg?: number;
  vit_k_ug?: number;
  // Minerals
  iron_mg?: number;
  calcium_mg?: number;
  magnesium_mg?: number;
  zinc_mg?: number;
  potassium_mg?: number;
  sodium_mg?: number;
  phosphorus_mg?: number;
  copper_mg?: number;
  manganese_mg?: number;
  selenium_ug?: number;
  iodine_ug?: number;
  // Other
  sugar_g?: number;
  saturated_fat_g?: number;
}

/**
 * NIH / FDA Recommended Daily Allowance for adults (combined male/female,
 * using the higher value for safety). Used for the "% of RDA" display.
 * These are guidance values, not medical recommendations.
 */
export const RDA: Required<NutrientPanel> = {
  // Vitamins
  vit_a_ug: 900,
  vit_b1_mg: 1.2,
  vit_b2_mg: 1.3,
  vit_b3_mg: 16,
  vit_b6_mg: 1.7,
  vit_b9_ug: 400,
  vit_b12_ug: 2.4,
  vit_c_mg: 90,
  vit_d_ug: 20,
  vit_e_mg: 15,
  vit_k_ug: 120,
  // Minerals
  iron_mg: 18,
  calcium_mg: 1000,
  magnesium_mg: 420,
  zinc_mg: 11,
  potassium_mg: 3400,
  sodium_mg: 2300, // upper limit, not a target
  phosphorus_mg: 700,
  copper_mg: 0.9,
  manganese_mg: 2.3,
  selenium_ug: 55,
  iodine_ug: 150,
  // Other — these are upper-limit / guidance, not targets
  sugar_g: 50,
  saturated_fat_g: 20
};

/** All nutrient keys in the order we want them shown in UI. */
export const NUTRIENT_KEYS: Array<keyof NutrientPanel> = [
  "vit_a_ug",
  "vit_c_mg",
  "vit_d_ug",
  "vit_e_mg",
  "vit_k_ug",
  "vit_b1_mg",
  "vit_b2_mg",
  "vit_b3_mg",
  "vit_b6_mg",
  "vit_b9_ug",
  "vit_b12_ug",
  "calcium_mg",
  "iron_mg",
  "magnesium_mg",
  "zinc_mg",
  "potassium_mg",
  "sodium_mg",
  "phosphorus_mg",
  "copper_mg",
  "manganese_mg",
  "selenium_ug",
  "iodine_ug",
  "sugar_g",
  "saturated_fat_g"
];

/** UI labels — short enough for a compact list. */
export const NUTRIENT_LABELS: Record<keyof NutrientPanel, string> = {
  vit_a_ug: "Vitamin A",
  vit_b1_mg: "B1 (Thiamine)",
  vit_b2_mg: "B2 (Riboflavin)",
  vit_b3_mg: "B3 (Niacin)",
  vit_b6_mg: "B6",
  vit_b9_ug: "Folate (B9)",
  vit_b12_ug: "B12",
  vit_c_mg: "Vitamin C",
  vit_d_ug: "Vitamin D",
  vit_e_mg: "Vitamin E",
  vit_k_ug: "Vitamin K",
  iron_mg: "Iron",
  calcium_mg: "Calcium",
  magnesium_mg: "Magnesium",
  zinc_mg: "Zinc",
  potassium_mg: "Potassium",
  sodium_mg: "Sodium",
  phosphorus_mg: "Phosphorus",
  copper_mg: "Copper",
  manganese_mg: "Manganese",
  selenium_ug: "Selenium",
  iodine_ug: "Iodine",
  sugar_g: "Sugar",
  saturated_fat_g: "Sat. Fat"
};

/** Group keys for the UI (Vitamins / Minerals / Other tabs). */
export const NUTRIENT_GROUPS = {
  Vitamins: [
    "vit_a_ug",
    "vit_c_mg",
    "vit_d_ug",
    "vit_e_mg",
    "vit_k_ug",
    "vit_b1_mg",
    "vit_b2_mg",
    "vit_b3_mg",
    "vit_b6_mg",
    "vit_b9_ug",
    "vit_b12_ug"
  ] as Array<keyof NutrientPanel>,
  Minerals: [
    "calcium_mg",
    "iron_mg",
    "magnesium_mg",
    "zinc_mg",
    "potassium_mg",
    "sodium_mg",
    "phosphorus_mg",
    "copper_mg",
    "manganese_mg",
    "selenium_ug",
    "iodine_ug"
  ] as Array<keyof NutrientPanel>,
  Other: ["sugar_g", "saturated_fat_g"] as Array<keyof NutrientPanel>
};

/** Format a nutrient value with its unit, fall back to "—" for missing. */
export function formatNutrient(key: keyof NutrientPanel, value: number | undefined): string {
  if (value === undefined || value === null || !Number.isFinite(value)) return "—";
  const unit = key.endsWith("_ug") ? "μg" : key.endsWith("_mg") ? "mg" : "g";
  // Smart rounding — small values get a decimal, big ones don't
  const rounded = value < 10 ? Math.round(value * 10) / 10 : Math.round(value);
  return `${rounded} ${unit}`;
}

/** Sum nutrients across an array of items (food items inside one meal). */
export function sumItemNutrients(
  items: Array<{ nutrients?: NutrientPanel }>
): NutrientPanel {
  const out: NutrientPanel = {};
  for (const key of NUTRIENT_KEYS) {
    let total = 0;
    let any = false;
    for (const item of items) {
      const v = item.nutrients?.[key];
      if (v !== undefined && Number.isFinite(v)) {
        total += v;
        any = true;
      }
    }
    if (any) out[key] = round1(total);
  }
  return out;
}

/** Sum nutrients across a day's worth of meals (across all items). */
export function sumDayNutrients(meals: LoggedMeal[]): NutrientPanel {
  const allItems = meals.flatMap((m) => m.estimate.items);
  return sumItemNutrients(allItems);
}

/** One contribution row for the "where is this nutrient coming from" UI. */
export interface NutrientSource {
  /** Item name (e.g. "Banana, medium") */
  itemName: string;
  /** Parent meal id — for jumping into the meal detail. */
  mealId: string;
  /** Meal type — used for grouping/icon display. */
  mealType: LoggedMeal["meal_type"];
  /** How much of this nutrient came from this item, in the nutrient's own unit. */
  value: number;
  /** Percentage of the day's total contributed by this item. */
  pctOfDay: number;
}

/**
 * Build a per-item contribution list for a specific nutrient across a day's
 * meals. Sorted descending by contribution. Items without data for that
 * nutrient are skipped. Used by the Nutrients page to answer "where is my
 * vitamin C coming from?"
 *
 * Returns an empty array when no item carries the nutrient — caller renders
 * a "no source data" hint in that case.
 */
export function nutrientSourcesForDay(
  meals: LoggedMeal[],
  nutrientKey: keyof NutrientPanel
): NutrientSource[] {
  const raw: Array<{ itemName: string; mealId: string; mealType: LoggedMeal["meal_type"]; value: number }> = [];
  let dayTotal = 0;

  for (const meal of meals) {
    for (const item of meal.estimate.items) {
      const v = item.nutrients?.[nutrientKey];
      if (v === undefined || !Number.isFinite(v) || v <= 0) continue;
      raw.push({
        itemName: item.name,
        mealId: meal.id,
        mealType: meal.meal_type,
        value: v
      });
      dayTotal += v;
    }
  }

  if (dayTotal <= 0) return [];

  return raw
    .sort((a, b) => b.value - a.value)
    .map((r) => ({
      itemName: r.itemName,
      mealId: r.mealId,
      mealType: r.mealType,
      value: round1(r.value),
      pctOfDay: Math.round((r.value / dayTotal) * 100)
    }));
}

/** Scale a NutrientPanel by a multiplier (used when quantity is adjusted). */
export function scaleNutrients(
  nutrients: NutrientPanel | undefined,
  quantity: number
): NutrientPanel | undefined {
  if (!nutrients || quantity === 1) return nutrients;
  const out: NutrientPanel = {};
  for (const key of NUTRIENT_KEYS) {
    const v = nutrients[key];
    if (v !== undefined && Number.isFinite(v)) {
      out[key] = round1(v * quantity);
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Percent of RDA, clamped 0–999 for display. */
export function percentOfRDA(key: keyof NutrientPanel, value: number | undefined): number | null {
  if (value === undefined || !Number.isFinite(value)) return null;
  const rda = RDA[key];
  if (!rda) return null;
  return Math.min(999, Math.round((value / rda) * 100));
}

/** Coverage helper — how many nutrients have data (0–25). */
export function coverageCount(nutrients: NutrientPanel | undefined): number {
  if (!nutrients) return 0;
  return NUTRIENT_KEYS.filter((k) => {
    const v = nutrients[k];
    return v !== undefined && Number.isFinite(v) && v > 0;
  }).length;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** Convenience: pull the panel out of a MealEstimate (sums across items). */
export function nutrientsForEstimate(estimate: MealEstimate): NutrientPanel {
  return sumItemNutrients(estimate.items);
}
